import { useRequiredContext } from "@redotech/react-util/context";
import { hasReturnsRestrictedAdminAccess } from "@redotech/redo-return-core-common/restricted-admin-access";
import {
  getReturnSelectableCarrierGroupsFor,
  getReturnSelectableCarriersForGroups,
  returnSelectableCarrierGroups,
} from "@redotech/redo-return-core-common/shipping/return-selectable-carriers";
import { SendcloudReturnDocumentType } from "@redotech/redo-return-core-common/shipping/sendcloud-return-document-type";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { FormMultiSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import classNames from "classnames";
import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../../app/auth";
import { DefaultWeightDisplay } from "../../label-rate/default-weight-display";
import { LinkCard } from "../component/link-card";
import { SettingCard } from "../component/setting-card";

const sendcloudReturnDocumentTypeOptions = [
  {
    id: SendcloudReturnDocumentType.BOTH,
    type: "text" as const,
    text: "QR codes and labels (default)",
    value: SendcloudReturnDocumentType.BOTH,
  },
  {
    id: SendcloudReturnDocumentType.QR_ONLY,
    type: "text" as const,
    text: "QR codes only",
    value: SendcloudReturnDocumentType.QR_ONLY,
  },
  {
    id: SendcloudReturnDocumentType.LABEL_ONLY,
    type: "text" as const,
    text: "Labels only",
    value: SendcloudReturnDocumentType.LABEL_ONLY,
  },
];

const sendcloudReturnDocumentTypeDescriptions: Record<
  SendcloudReturnDocumentType,
  string
> = {
  [SendcloudReturnDocumentType.BOTH]:
    "Offers whichever Sendcloud services a route publishes. Sendcloud sells QR codes and printable labels separately, and not every route offers both.",
  [SendcloudReturnDocumentType.QR_ONLY]:
    "Only offers Sendcloud services that produce a QR code. Routes without one will have no Sendcloud rate and fall back to another carrier, which usually costs more.",
  [SendcloudReturnDocumentType.LABEL_ONLY]:
    "Only offers Sendcloud services that produce a printable label. Routes whose only Sendcloud service is QR-only will fall back to another carrier, which usually costs more.",
};

export const shippingForm = groupInput({
  carrierOverride: input<string[]>({ equal: arrayEqual(stringEqual) }),
  includeMultipleCarrierOptions: input<boolean>(),
  insured: input<boolean>(),
  passThroughReturnLabelAdjustments: input<boolean>(),
  pickupEnabled: input<boolean>(),
  pickupEnabledForHeavyItems: input<boolean>(),
  pickupMaxLabelUpgradeRange: input<string>(),
  canEditReturnAddress: input<boolean>(),
  generateQrCodes: input<boolean>(),
  qrCodeOnlyShipping: input<boolean>(),
  sendcloudReturnDocumentType: input<SendcloudReturnDocumentType>(),
  fedexQrCodesEnabledForMerchantRates: input<boolean>(),
  useFixedPackageDimensions: input<boolean>(),
  fixedPackageDimensions: groupInput({
    length: input<string>(),
    width: input<string>(),
    height: input<string>(),
    weight: input<string>(),
  }),
});

export type ShippingForm = InputSchema.Input<typeof shippingForm>;

export type ShippingValue = InputSchema.Value<typeof shippingForm>;

export const shippingDefault: ShippingValue = {
  carrierOverride: [],
  includeMultipleCarrierOptions: false,
  insured: false,
  passThroughReturnLabelAdjustments: false,
  pickupEnabled: false,
  pickupEnabledForHeavyItems: false,
  pickupMaxLabelUpgradeRange: "",
  canEditReturnAddress: false,
  generateQrCodes: false,
  qrCodeOnlyShipping: false,
  sendcloudReturnDocumentType: SendcloudReturnDocumentType.BOTH,
  fedexQrCodesEnabledForMerchantRates: false,
  useFixedPackageDimensions: false,
  fixedPackageDimensions: { length: "", width: "", height: "", weight: "" },
};

export const ReturnShippingSettings = memo(function ReturnShippingSettings({
  input,
  redoMaxLabelPriceInput,
  multipleLabelsEnabledInput,
}: {
  input: ShippingForm;
  redoMaxLabelPriceInput?: { setValue: (value: string) => void; value: string };
  multipleLabelsEnabledInput?: {
    setValue: (value: boolean) => void;
    value: boolean;
  };
}) {
  const auth = useRequiredContext(AuthContext);
  const hasRestrictedAdminAccess = hasReturnsRestrictedAdminAccess(auth.email);
  const canSeeRedoMaxLabelPrice =
    redoMaxLabelPriceInput && hasRestrictedAdminAccess;
  const navigate = useNavigate();
  const {
    carrierOverride,
    includeMultipleCarrierOptions,
    passThroughReturnLabelAdjustments,
    pickupEnabled,
    pickupEnabledForHeavyItems,
    pickupMaxLabelUpgradeRange,
    insured,
    canEditReturnAddress,
    generateQrCodes,
    qrCodeOnlyShipping,
    sendcloudReturnDocumentType,
    fedexQrCodesEnabledForMerchantRates,
    useFixedPackageDimensions,
    fixedPackageDimensions,
  } = input.inputs;

  // The cap stores rate.carrier strings; the admin picks brands, exactly as the
  // location picker does, and a pick writes every string that brand governs.
  const { value: carrierOverrideValue } = carrierOverride;
  const carrierGroupsInput = useMemo(
    () => ({
      ...carrierOverride,
      value: getReturnSelectableCarrierGroupsFor(carrierOverrideValue),
      setValue: (groups: string[]) =>
        carrierOverride.setValue(getReturnSelectableCarriersForGroups(groups)),
    }),
    [carrierOverride, carrierOverrideValue],
  );

  return (
    <Flex dir="column" gap="3xl">
      <FormMultiSelectDropdown
        description={
          carrierOverride.value.length > 0
            ? "Enabled shipping carriers. This carrier override takes precedence over QR code only shipping."
            : "Enabled shipping carriers"
        }
        display={(groups) => {
          if (!carrierOverrideValue.length) {
            return "All";
          }
          if (!groups.length) {
            return "Unrecognized carriers only";
          }
          return groups.length === returnSelectableCarrierGroups.length
            ? "All"
            : groups.join(", ");
        }}
        input={carrierGroupsInput}
        label="Carriers"
        options={returnSelectableCarrierGroups.map((group) => group.label)}
      >
        {(group) => group}
      </FormMultiSelectDropdown>

      <RedoToggle
        description="If enabled, customers pick from the eligible return carriers in the returns portal instead of always being given the cheapest covered one. If the shopper picks a pricier carrier and it can't be purchased when the label is created, the label falls back to the cheapest available carrier and the swap is noted on the return's timeline."
        label="Let shoppers choose a return carrier"
        setValue={includeMultipleCarrierOptions.setValue}
        size="sm"
        value={includeMultipleCarrierOptions.value}
      />

      <RedoToggle
        description="Enable shipping insurance. Increases label price by 0.5% of items value (min $0.50)."
        label="Shipping insurance"
        setValue={insured.setValue}
        size="sm"
        value={insured.value}
      />
      <RedoToggle
        description="Pass through carrier label adjustments to merchants. When enabled, any adjustments from the carrier (such as address corrections or dimensional weight changes) will be charged to the merchant."
        label="Pass through return label adjustments"
        setValue={passThroughReturnLabelAdjustments.setValue}
        size="sm"
        value={passThroughReturnLabelAdjustments.value}
      />

      <RedoToggle
        description="Offer return package pickup, for the cost of label upgrade plus $2"
        label="Return pickup"
        setValue={pickupEnabled.setValue}
        size="sm"
        value={pickupEnabled.value}
      />

      {pickupEnabled.value && (
        <>
          <RedoToggle
            description="Allow package pickup for all items regardless of weight limits (over 9 lbs)"
            label="Allow package pickup for heavy items"
            setValue={pickupEnabledForHeavyItems.setValue}
            size="sm"
            value={pickupEnabledForHeavyItems.value}
          />

          <RedoTextInput
            description="Maximum range for package pickup label upgrade"
            IconLeading={() => "$"}
            inputTypeHint="number"
            label="Pickup max label upgrade range"
            min={0.01}
            setValue={pickupMaxLabelUpgradeRange.setValue}
            step={0.01}
            value={pickupMaxLabelUpgradeRange.value}
          />
        </>
      )}
      {canSeeRedoMaxLabelPrice && redoMaxLabelPriceInput && (
        <RedoTextInput
          description="Maximum price (in USD) that Redo will cover for return labels when a return is covered"
          IconLeading={() => "$"}
          inputTypeHint="number"
          label="Redo max label coverage price"
          max={2000}
          min={1}
          setValue={redoMaxLabelPriceInput.setValue}
          value={redoMaxLabelPriceInput.value}
        />
      )}
      {hasRestrictedAdminAccess && multipleLabelsEnabledInput && (
        <RedoToggle
          description="Lets the merchant choose how many labels to generate, and which items go in each, when approving a return in manual review. Increases Redo's label costs; requires Finance approval."
          label="Multiple labels per return"
          setValue={multipleLabelsEnabledInput.setValue}
          size="sm"
          value={multipleLabelsEnabledInput.value}
        />
      )}
      <RedoToggle
        description="Allows Merchant to change return address during return approval"
        label="Change return address"
        setValue={canEditReturnAddress.setValue}
        size="sm"
        value={canEditReturnAddress.value}
      />
      <RedoToggle
        description="Generate QR codes for return labels when the carrier supports them. Required for QR code only shipping to work."
        label="Generate QR codes"
        setValue={generateQrCodes.setValue}
        size="sm"
        value={generateQrCodes.value}
      />
      <RedoToggle
        description={
          carrierOverride.value.length > 0
            ? "Restrict rates to QR-supporting carriers and only show the QR code to customers. The label is shown as a fallback if QR generation fails. This may increase label costs since cheaper non-QR carriers will be excluded. Currently inactive because a carrier override is set above."
            : "Restrict rates to QR-supporting carriers and only show the QR code to customers. The label is shown as a fallback if QR generation fails. This may increase label costs since cheaper non-QR carriers will be excluded."
        }
        label="QR code only shipping"
        setValue={qrCodeOnlyShipping.setValue}
        size="sm"
        value={qrCodeOnlyShipping.value}
      />
      <RedoToggle
        description="Enable FedEx QR code generation for merchant-created labels. Merchants must ensure FedEx QR code support is enabled on their EasyPost account."
        label="FedEx QR codes (merchant labels)"
        setValue={fedexQrCodesEnabledForMerchantRates.setValue}
        size="sm"
        value={fedexQrCodesEnabledForMerchantRates.value}
      />
      <RedoSingleSelectDropdownInput<SendcloudReturnDocumentType>
        description={
          sendcloudReturnDocumentTypeDescriptions[
            sendcloudReturnDocumentType.value
          ]
        }
        fullWidth
        label="Sendcloud return format"
        options={sendcloudReturnDocumentTypeOptions}
        optionSelected={({ item }) =>
          sendcloudReturnDocumentType.setValue(item.value)
        }
        selectedItem={sendcloudReturnDocumentTypeOptions.find(
          (option) => option.value === sendcloudReturnDocumentType.value,
        )}
        size="sm"
      />

      <SettingCard>
        <Flex align="stretch" dir="column" gap="none">
          <LinkCard
            onClick={() => navigate("../label-rate-overrides")}
            subtitle="Configure merchant-specific rate calculation rules"
            title="Label rate calculation overrides"
          />
        </Flex>
      </SettingCard>

      <DefaultWeightDisplay />
      <RedoToggle
        description="Use standard dimensions for all return labels instead of calculating from product weight"
        label="Use fixed package dimensions"
        setValue={useFixedPackageDimensions.setValue}
        size="sm"
        value={useFixedPackageDimensions.value}
      />
      {useFixedPackageDimensions.value && (
        // design-allow: existing CSS grid for the four dimension fields
        <div className={gridCss.grid}>
          {/* design-allow: existing CSS grid for the four dimension fields */}
          <div className={classNames(gridCss.span12, gridCss.span3M)}>
            <FormTextInput
              input={fixedPackageDimensions.inputs.length}
              label="Length (in)"
              step="0.1"
              type="number"
            />
          </div>
          {/* design-allow: existing CSS grid for the four dimension fields */}
          <div className={classNames(gridCss.span12, gridCss.span3M)}>
            <FormTextInput
              input={fixedPackageDimensions.inputs.width}
              label="Width (in)"
              step="0.1"
              type="number"
            />
          </div>
          {/* design-allow: existing CSS grid for the four dimension fields */}
          <div className={classNames(gridCss.span12, gridCss.span3M)}>
            <FormTextInput
              input={fixedPackageDimensions.inputs.height}
              label="Height (in)"
              step="0.1"
              type="number"
            />
          </div>
          {/* design-allow: existing CSS grid for the four dimension fields */}
          <div className={classNames(gridCss.span12, gridCss.span3M)}>
            <FormTextInput
              input={fixedPackageDimensions.inputs.weight}
              label="Weight (oz)"
              step="0.1"
              type="number"
            />
          </div>
        </div>
      )}
    </Flex>
  );
});
